import { SizesProvider } from '@/state/sizesProvider';

import { AtClickable, AtIcon, AtMedia, AtText } from '@/components/atoms';
import {
  TClickableSize,
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { MlCopyBlock } from '@/components/molecules';
import {
  EBlockAlignment,
  EThemes,
  IOrContentMediaBlockCSProps,
} from '@/lib/contentstack-sdk/types/component';
import atClickable from '@/components/atoms/at-clickable/at-clickable.variants';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import { OrContentMediaBlock } from './or-content-media-block';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import {
  EOrContentMediaBlockSize,
  TOrContentMediaBlockCS,
} from './or-content-media-block.types';

export const OrContentMediaBlockCS = ({ block }: TOrContentMediaBlockCS) => {
  if (!block) return null;
  const {
    theme,
    left_content,
    right_content,
    media_size,
    top_spacing,
    bottom_spacing,
    _content_type_uid: contentType,
  } = block;
  const RenderLeftContent = () => {
    const imageProps = parseImageFromCMS(left_content);

    return (
      imageProps?.src && (
        <AtMedia
          {...imageProps}
          sizes={
            media_size === 'LG'
              ? '(max-width: 1023px) 100vw, 75vw'
              : '(max-width: 1023px) 100vw, 50vw'
          }
        />
      )
    );
  };
  const currentTheme = EThemes[theme?.theme] as EThemes;
  return (
    <SizesProvider contentType={contentType}>
      <OrContentMediaBlock
        size={
          EOrContentMediaBlockSize[
            media_size as keyof typeof EOrContentMediaBlockSize
          ]
        }
        theme={currentTheme}
        topSpacing={top_spacing?.spacing_value}
        bottomSpacing={bottom_spacing?.spacing_value}
        topDivider={top_spacing?.divider}
        bottomDivider={bottom_spacing?.divider}
        RenderLeftContent={RenderLeftContent}
      >
        <>
          {right_content?.length > 0 ? (
            <>
              {right_content.map((contents) => {
                const {
                  title,
                  description,
                  heading_element,
                  uid,
                  alignment,
                  link,
                  eyebrow,
                  subhead,
                  heading_variant,
                  block_header,
                } = contents;

                const {
                  size,
                  icon,
                  outline,
                  target,
                  url: { title: label, href },
                  variant,
                  type,
                  aria_label,
                } = link;

                const iconName = icon?.[0]?.name?.icon;
                const iconPosition = icon?.[0]?.select?.iconPosition;
                const currentIcon = iconName as EIcons;
                const currentTarget = target as keyof typeof EClickableTarget;
                const currentVariant =
                  variant as keyof typeof EClickableVariants;
                const renderIcon =
                  icon &&
                  icon.length > 0 &&
                  EClickableTarget[currentTarget] !== EClickableTarget.BLANK;
                const { iconColor } = atClickable({
                  type,
                  intent: EClickableVariants[currentVariant],
                  outline,
                });

                return (
                  <MlCopyBlock
                    header={block_header}
                    title={title}
                    key={uid}
                    eyebrow={eyebrow}
                    subhead={subhead}
                    titleElement={heading_element?.select || 'h2'}
                    titleVariant={
                      heading_variant?.select || heading_element?.select || 'h2'
                    }
                    alignment={EBlockAlignment[alignment?.alignmentprop]}
                  >
                    <RenderRichText body={description} />
                    <AtClickable
                      variant={EClickableVariants[currentVariant]}
                      size={size.toLowerCase() as TClickableSize}
                      label={label}
                      href={href}
                      iconPosition={iconPosition}
                      outline={outline}
                      type={type}
                      target={EClickableTarget[currentTarget]}
                      ariaLabel={aria_label}
                      interactLocation="or-content-media-block"
                    >
                      {renderIcon && (
                        <AtIcon color={iconColor()} icon={currentIcon} />
                      )}
                    </AtClickable>
                  </MlCopyBlock>
                );
              })}
            </>
          ) : (
            <AtText className="text-skin-error">
              Please add a Copy Block Instance
            </AtText>
          )}
        </>
      </OrContentMediaBlock>
    </SizesProvider>
  );
};
